import { appendQueryParam, appendVariant, buildChatAssetUrl } from '../composer/chatAssetUtils';
import {
  hasMissingChatFileStorage,
  hasMissingChatImageStorage,
} from '../composer/chatAttachmentAvailability';
import { shouldExcludeAdminSenderForMessage } from './adminSenderExclusionUtils';
import { formatAdminDisplayName } from '../../../../utils/adminDisplayName';
import {
  buildPersonAvatarUrl,
  getPersonInitial,
} from '../../avatar/PersonAvatar';
import { artistGenderAvatarBg } from '../avatars/avatarColor';

const normalizeString = (value) => String(value == null ? '' : value).trim();

const isSameId = (left, right) => {
  const normalizedLeft = normalizeString(left);
  const normalizedRight = normalizeString(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
};

export const buildGroupChatMessagePresentation = ({
  message,
  viewerRole = 'admin',
  currentAdminId = '',
  currentArtistId = '',
  apiUrl = '',
  paths = {},
  withDownloadQuery = false,
}) => {
  const row = message || {};
  const senderType = normalizeString(row.sender_type).toLowerCase();
  const isAdminSender = senderType === 'admin' || Boolean(row.admin_user_id);
  const isArtistSender = senderType === 'artist';
  const excludeSenderMeta =
    viewerRole === 'admin' && shouldExcludeAdminSenderForMessage(row);

  const isSelf =
    excludeSenderMeta
      ? false
      : viewerRole === 'admin'
      ? isAdminSender && isSameId(row.admin_user_id, currentAdminId)
      : isArtistSender && isSameId(row.artist_user_id, currentArtistId);
  const senderName = isAdminSender
    ? formatAdminDisplayName({
        adminDisplayName: row.admin_display_name || '',
        fullName: row.admin_full_name || '',
        username: row.admin_username || '',
        role: row.admin_role || '',
        isExternalAccount: row.admin_is_external_account === true,
        externalAffiliationName: row.admin_external_affiliation_name || '',
        useAsciiParentheses: true,
      })
    : normalizeString(
        row.artist_name ||
          row.artist_name_en ||
          (viewerRole === 'artist' && isSelf ? 'あなた' : 'アーティスト')
      ) || 'アーティスト';
  const avatarLabel = excludeSenderMeta
    ? (isAdminSender ? 'Tacticart' : senderName)
    : senderName;

  const readCount = Math.max(0, (Number(row.read_count) || 0) - 1);
  const hasMissingImageStorage = hasMissingChatImageStorage(row);
  const hasMissingFileStorage = hasMissingChatFileStorage(row);

  const imageUrl = row.image_file_name
    ? hasMissingImageStorage
      ? ''
      : appendVariant(
        buildChatAssetUrl({ apiUrl, root: paths.messageImagePath || '', file: row.image_file_name }),
        'proxy'
      )
    : '';
  const imageGroupItems = Array.isArray(row.image_group_items) && row.image_group_items.length > 1
    ? row.image_group_items.map((item) => {
      const missing = hasMissingChatImageStorage(item);
      const baseUrl = item?.image_file_name
        ? buildChatAssetUrl({ apiUrl, root: paths.messageImagePath || '', file: item.image_file_name })
        : '';
      return {
        messageId: item?.id,
        imageId: item?.image_id,
        fileName: item?.image_file_name,
        src: missing || !baseUrl ? '' : appendVariant(baseUrl, 'proxy'),
        fullSrc: missing || !baseUrl ? '' : appendVariant(baseUrl, 'raw'),
        width: item?.image_width,
        height: item?.image_height,
      };
    })
    : [];

  const rawFileUrl = row.file_file_name
    ? hasMissingFileStorage
      ? ''
      : buildChatAssetUrl({ apiUrl, root: paths.messageFilePath || '', file: row.file_file_name })
    : '';
  const fileUrl = withDownloadQuery
    ? appendQueryParam(rawFileUrl, 'download', '1')
    : rawFileUrl;

  const avatarFile = isAdminSender ? row.admin_profile_picture : row.artist_profile_picture;
  const avatarRoot = isAdminSender ? paths.adminProfilePath : paths.artistProfilePath;
  const avatarUrl = buildPersonAvatarUrl({
    role: isAdminSender ? 'admin' : 'artist',
    profilePicture: avatarFile,
    variant: 'proxy',
    apiUrl,
    root: avatarRoot || '',
  });

  const avatarInitial = getPersonInitial(avatarLabel, '?');
  const avatarStyle = avatarUrl
    ? undefined
    : isAdminSender
      ? {
        background: 'var(--admin-avatar-fallback-bg)',
        color: 'var(--admin-avatar-fallback-text)',
      }
      : { backgroundColor: artistGenderAvatarBg(row.artist_gender || row.gender) };

  return {
    isAdminSender,
    isArtistSender,
    isSelf,
    senderName: excludeSenderMeta ? '' : senderName,
    readCount,
    imageUrl,
    imageGroupItems,
    fileUrl,
    hasMissingImageStorage,
    hasMissingFileStorage,
    avatarUrl,
    avatarInitial,
    avatarStyle,
    showSender: !excludeSenderMeta,
    showAvatar: true,
    showAdminSenderMeta: false,
  };
};

export const canUnsendGroupChatMessage = ({
  message,
  viewerRole = 'admin',
  currentAdminId = '',
  currentArtistId = '',
  allowedFormats = ['text', 'image', 'file'],
}) => {
  const row = message || {};
  const senderType = normalizeString(row.sender_type).toLowerCase();
  const format = normalizeString(row.message_format).toLowerCase();
  if (!allowedFormats.includes(format)) return false;

  if (viewerRole === 'admin') {
    if (senderType !== 'admin') return false;
    return isSameId(row.admin_user_id, currentAdminId);
  }

  if (viewerRole === 'artist') {
    if (senderType !== 'artist') return false;
    return isSameId(row.artist_user_id, currentArtistId);
  }

  return false;
};
